<script setup lang="ts">
/** POST /projects/{id}/models. Everything shown here comes from /datasets and /estimate. */
import Dialog from '~/components/ui/Dialog.vue'
import Choice from '~/components/ui/Choice.vue'
import PickList from '~/components/ui/PickList.vue'
import Field from '~/components/ui/Field.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import EmptyState from '~/components/ui/EmptyState.vue'
import StatusCell from '~/components/ui/StatusCell.vue'
import SelectField from '~/components/ui/SelectField.vue'
import Skeleton from '~/components/ui/Skeleton.vue'
import ParamRow from '~/components/inspector/ParamRow.vue'
import ActionLink from '~/components/ui/ActionLink.vue'
import { HYPERPARAM_KNOBS, overridesToRequest, type HyperparamKnob, type HyperparamOverrides } from '~/utils/hyperparams'
import { boundRangeLabel, trainHyperparamErrors, type HyperparamBounds, type TrainHyperparams } from '~/utils/trainForm'
import type { components } from '@scalty/contracts'

type TrainSet = { id: string; name: string; facts: string; status: string; kind?: string; columns?: string[]; preparation?: string }
const props = withDefaults(defineProps<{
  ready?: boolean
  /** False until the project's datasets, catalog and machines have answered. */
  loading?: boolean
  sets: TrainSet[]
  /** Train from a dataset page: the dataset is already chosen, so it is stated instead of picked. */
  fixed?: TrainSet | null
  /** True when the caller already stands on a dataset, so `fixed` is what will arrive. Known from
   *  the pressed control rather than from the read, which is why the waiting form can reserve the
   *  one-line statement instead of the taller picker. */
  fixedExpected?: boolean
  /** Why the training figures are missing, in the server's own words; never a number stood in for. */
  note?: string
  accelerators: { id: string; name: string; facts: string }[]
  /** GET /models/catalog: the bases a text model can start from. Only the ones that can actually
   *  be picked here are passed in — an entry nobody can choose is not a choice (note 5).
   *  `servedHere` is the contract's `servedOnPool`: whether a model trained on this base can be
   *  published and talked to in this environment, as opposed to downloaded and run elsewhere.
   *  `bounds` is the entry's `hyperparamBounds`, the only source of the training settings' ranges. */
  bases: { id: string; name: string; facts: string; servedHere?: boolean; bounds?: HyperparamBounds }[]
  estimate: { label: string; value: string }[]
  /** Said before the run starts when the projection takes the month past the limit in force;
   *  empty when it fits. The figures come from the same balance the entitlement gate enforces,
   *  so the sentence is never a guess about what will happen. */
  limitWarning?: string
  /** The configuration the estimate resolved for this run, overrides included; null while it has
   *  not answered or when it refused. The settings tab shows it as what the platform chose. */
  resolved?: components['schemas']['Hyperparams'] | null
}>(), { ready: true, loading: false, fixed: null, fixedExpected: false, note: '', limitWarning: '', resolved: null })
const emit = defineEmits<{ close: []; submit: [payload: Record<string, unknown>]; createDataset: []; change: [payload: { datasetId: string; acceleratorClass: string; baseModel: string; name: string; modelType: string; targetColumn: string; hyperparams?: components['schemas']['Hyperparams'] }] }>()

/* How many placeholder rows the waiting form draws. The heights are held by the reserves in
   `scalty-theme.css`, so these numbers only decide how the waiting form reads, never how tall it
   is: four cells fill the two grid rows the bases and the machines settle into. */
const RESERVED_SET_ROWS = 3
const RESERVED_BASE_CHOICES = 4
const RESERVED_MACHINE_CHOICES = 4
/* The estimate's six rows: time and cost, the balance after, and the four resolved settings. */
const RESERVED_ESTIMATE_ROWS = 6

const set = ref(props.fixed?.id ?? props.sets[0]?.id ?? '')
const accelerator = ref(props.accelerators[0]?.id ?? '')
/* The base this environment serves is the one a customer can publish and talk to, so it is what
   the dialog offers first. It is read from the catalog, not named here: a hard-coded id would go
   stale the moment the environment's pool hosts another base. */
const servedBase = () => props.bases.find((base) => base.servedHere)?.id ?? props.bases[0]?.id ?? ''
const baseModel = ref(servedBase())
const name = ref(props.fixed?.name ?? props.sets[0]?.name ?? '')
/* The options arrive after the dialog opens, so the first real choice settles the fields the
   customer never touched. Anything they did touch is left exactly as they left it. */
const touched = ref(false)
watch(() => [props.sets, props.accelerators, props.bases, props.fixed] as const, () => {
  if (touched.value) return
  set.value = props.fixed?.id ?? props.sets[0]?.id ?? ''
  name.value = props.fixed?.name ?? props.sets[0]?.name ?? ''
  accelerator.value = props.accelerators[0]?.id ?? ''
  baseModel.value = servedBase()
})
const selected = computed(() => props.fixed ?? props.sets.find((item) => item.id === set.value))
/* With the dataset settled the dialog always has something to train on, whatever else the
   project holds. */
const choosable = computed(() => Boolean(props.fixed) || props.sets.length > 0)
const modelType = ref('tabular_classification')
const targetColumn = ref('')
/* Tabular data trains a predictor from its own columns; documents and conversations train a text
   model from a base. The fields follow the kind of the chosen dataset, so a choice that does not
   apply to it is never left standing (note 13). */
const tabular = computed(() => selected.value?.kind === 'tabular')
/* The professional depth of the same dialog (PRODUCT_MODEL, "One path, two depths"): the five
   training settings, each empty until the customer sets one. An empty setting is chosen by the
   platform from the data — sending a default of ours would defeat that — and a set one goes into
   the estimate, so the time and cost under the form follow it. The ranges are the chosen base's
   catalog bounds and nothing else: the portal keeps no copy of them (W9-PORTAL-UX). */
const tab = ref<'model' | 'settings'>('model')
const KNOBS: { knob: HyperparamKnob; label: string }[] = [
  { knob: 'rank', label: 'Rank' },
  { knob: 'alpha', label: 'Alpha' },
  { knob: 'learningRate', label: 'Learning rate' },
  { knob: 'epochs', label: 'Epochs' },
  { knob: 'maxSeqLen', label: 'Sequence length' },
]
const knobText = reactive<Record<HyperparamKnob, string>>({ rank: '', alpha: '', learningRate: '', epochs: '', maxSeqLen: '' })
const bounds = computed(() => props.bases.find((base) => base.id === baseModel.value)?.bounds ?? null)
const knobValues = computed<TrainHyperparams>(() => {
  const values: TrainHyperparams = { rank: null, alpha: null, learningRate: null, epochs: null, maxSeqLen: null }
  for (const { knob } of KNOBS) {
    const text = knobText[knob].trim()
    if (text !== '' && Number.isFinite(Number(text))) values[knob] = Number(text)
  }
  return values
})
const knobErrors = computed(() => {
  const errors: Partial<Record<HyperparamKnob, string>> = { ...trainHyperparamErrors(knobValues.value, bounds.value) }
  for (const { knob, label } of KNOBS) {
    if (knobText[knob].trim() !== '' && knobValues.value[knob] === null) errors[knob] = `${label} must be a number.`
  }
  return errors
})
const knobHint = (knob: HyperparamKnob) => {
  const bound = bounds.value?.[knob]
  return bound ? boundRangeLabel(bound) ?? undefined : undefined
}
const knobPlaceholder = (knob: HyperparamKnob) => props.resolved?.[knob] != null ? `${props.resolved[knob]} · chosen by the platform` : 'chosen by the platform'
const overrides = computed<HyperparamOverrides>(() => {
  const out: HyperparamOverrides = {}
  for (const { knob } of KNOBS) {
    const value = knobValues.value[knob]
    if (value !== null && !knobErrors.value[knob]) out[knob] = value
  }
  return out
})
const knobsInvalid = computed(() => Object.keys(knobErrors.value).length > 0)
const overrideKey = computed(() => HYPERPARAM_KNOBS.map((knob) => overrides.value[knob] ?? '').join('|'))
watch([set, accelerator, baseModel, name, modelType, targetColumn, overrideKey], () => emit('change', { datasetId: set.value, acceleratorClass: accelerator.value, baseModel: baseModel.value, name: name.value, modelType: modelType.value, targetColumn: targetColumn.value, ...(tabular.value ? {} : { hyperparams: overridesToRequest(overrides.value) }) }), { immediate: true })
</script>

<template>
  <!-- One width AND one height from the first frame: the dialog opens with every field it will
       have, each one a placeholder in the exact box its value takes, and fills them in place. It
       never starts as a small box around a loading label and then grows (note 4, W8-UI-01). -->
  <Dialog title="Train model" :meta="loading ? undefined : fixed ? fixed.name : choosable ? 'a new model from your data' : 'a dataset is needed first'" :width="560" @close="$emit('close')">
    <template #icon><Icon name="models" /></template>
    <!-- The switch lives in the header, as a card header's one link action, so the second view
         costs the form no row: the dialog keeps the height it had with one view. -->
    <template v-if="!loading && choosable && !tabular" #aside>
      <ActionLink v-if="tab === 'model'" variant="header" label="Training settings" @click="tab = 'settings'"><template #icon><Icon name="sliders" /></template></ActionLink>
      <ActionLink v-else variant="header" label="Model" @click="tab = 'model'"><template #icon><Icon name="models" /></template></ActionLink>
    </template>

    <!-- The waiting form: the same rows in the same order, so nothing under them moves when the
         project answers. The dataset row follows the door the dialog was opened from, which is
         known from the press rather than from the read. -->
    <template v-if="loading">
      <div class="flex min-h-[var(--train-name-field)] flex-col gap-1.5">
        <span class="text-xs text-muted-foreground">Version name</span>
        <Skeleton height="var(--control-height)" />
        <Skeleton width="var(--measure-160px)" height="var(--skeleton-line)" />
      </div>

      <div class="flex flex-col gap-1.5">
        <span class="text-xs text-muted-foreground">Dataset</span>
        <!-- From a dataset page the row is the one dataset, and the waiting form reserves exactly
             it. From the menu it is a picker over however many datasets the project holds, which
             only the read can say: three rows are reserved, and a longer list settles taller. -->
        <Choice v-if="fixedExpected" placeholder class="min-h-[var(--train-choice-row)]" />
        <div v-else class="flex flex-col gap-1.5">
          <Choice v-for="row in RESERVED_SET_ROWS" :key="row" placeholder class="min-h-[var(--train-choice-row)]" />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <span class="text-xs text-muted-foreground">Base model</span>
        <div class="grid min-h-[var(--train-base-grid)] gap-1.5 md:grid-cols-2">
          <Choice v-for="row in RESERVED_BASE_CHOICES" :key="row" placeholder />
        </div>
      </div>
      <div class="flex flex-col gap-1.5">
        <span class="text-xs text-muted-foreground">Accelerator</span>
        <div class="grid min-h-[var(--train-machine-grid)] gap-1.5 md:grid-cols-2">
          <Choice v-for="row in RESERVED_MACHINE_CHOICES" :key="row" placeholder />
        </div>
      </div>

      <div class="flex h-[var(--train-estimate-height)] shrink-0 flex-col gap-[var(--measure-7px)] overflow-auto rounded-md border border-border p-2.5">
        <div class="flex flex-1 flex-col justify-around">
          <Skeleton v-for="row in RESERVED_ESTIMATE_ROWS" :key="row" :width="row % 2 ? 'var(--measure-160px)' : 'var(--measure-130px)'" />
        </div>
      </div>
    </template>

    <!-- A run trains on a dataset, so with none in the project the dialog offers to make one
         instead of showing a form that cannot be submitted (DR-20, design set of 2026-09-11). It
         opened the retired training-set dialog until W9-PORTAL-UX: the one place data goes is the
         dataset, and training prepares what it needs from it. -->
    <div v-else-if="!choosable" class="rounded-md border border-border">
      <EmptyState title="Nothing to train on in this project" hint="A run trains on one dataset. Create a dataset, put files in it, and training prepares what it needs.">
        <template #icon><Icon name="datasets" :size="16" /></template>
        <template #action>
          <ActionButton label="New dataset" variant="primary" @click="$emit('createDataset')"><template #icon><Icon name="plus" /></template></ActionButton>
        </template>
      </EmptyState>
    </div>

    <template v-else>
    <!-- Both views share one cell, so switching them never resizes the dialog: the view not shown
         keeps its place but is invisible, and so out of reach of the keyboard and screen readers. -->
    <div class="grid">
    <div class="flex flex-col gap-3 [grid-area:1/1]" :class="{ invisible: tab === 'settings' && !tabular }">
    <div class="flex min-h-[var(--train-name-field)] flex-col">
      <Field v-model="name" label="Version name" hint="The version number is assigned automatically" @update:model-value="touched = true" />
    </div>

    <!-- One name for one thing, whichever door the dialog was opened from (note 12). -->
    <div v-if="fixed" class="flex flex-col gap-1.5">
      <span class="text-xs text-muted-foreground">Dataset</span>
      <Choice :title="fixed.name" :facts="fixed.facts" selected class="min-h-[var(--train-choice-row)]" />
    </div>
    <div v-else class="flex flex-col gap-1.5">
      <span class="text-xs text-muted-foreground">Dataset</span>
      <!-- Searchable from the second entry: a project with thirty-five datasets is found by
           typing, not by scrolling a wall of them (note 10). -->
      <PickList v-slot="{ shown }" :items="sets" unit="datasets" :search-from="2" height="var(--measure-240px)">
        <Choice v-for="dataset in shown" :key="dataset.id" :title="dataset.name" :facts="dataset.facts" :selected="set === dataset.id" @click="set = dataset.id; touched = true">
          <span class="ml-auto shrink-0"><StatusCell :status="dataset.status" /></span>
        </Choice>
      </PickList>
    </div>
    <!-- What the teacher makes of a documents set, before the press (W9 census No. 4). -->
    <p v-if="selected?.preparation" class="m-0 text-xs leading-relaxed text-pretty text-muted-foreground">{{ selected.preparation }}</p>

    <div v-if="tabular" class="grid gap-3 md:grid-cols-2">
      <SelectField v-model="modelType" label="Task" :options="[{value:'tabular_classification',label:'Classification'},{value:'tabular_regression',label:'Regression'}]" @update:model-value="touched = true" />
      <SelectField v-model="targetColumn" label="Column to predict" :options="[{value:'',label:'Choose a column'},...(selected?.columns ?? []).map((column)=>({value:column,label:column}))]" @update:model-value="touched = true" />
    </div>
    <template v-else>
      <div class="flex flex-col gap-1.5">
        <span class="text-xs text-muted-foreground">Base model</span>
        <p v-if="!bases.length" class="m-0 text-xs text-destructive">No base model is available for training right now, so a run cannot start.</p>
        <div v-else class="grid min-h-[var(--train-base-grid)] gap-1.5 md:grid-cols-2">
          <Choice v-for="b in bases" :key="b.id" :title="b.name" :facts="b.facts" :selected="baseModel === b.id" @click="baseModel = b.id; touched = true" />
        </div>
      </div>
      <div class="flex flex-col gap-1.5">
        <span class="text-xs text-muted-foreground">Accelerator</span>
        <div class="grid min-h-[var(--train-machine-grid)] gap-1.5 md:grid-cols-2">
          <Choice v-for="a in accelerators" :key="a.id" :title="a.name" :facts="a.facts" :selected="accelerator === a.id" @click="accelerator = a.id; touched = true" />
        </div>
      </div>
    </template>
    </div>
    <div v-if="!tabular" class="grid content-start gap-3 [grid-area:1/1] md:grid-cols-2" :class="{ invisible: tab !== 'settings' }">
      <Field v-for="field in KNOBS" :key="field.knob" v-model="knobText[field.knob]" :label="field.label" inputmode="decimal" :placeholder="knobPlaceholder(field.knob)" :hint="knobHint(field.knob)" :error="knobErrors[field.knob]" />
    </div>
    </div>

    <!-- What the press costs and how long it takes, in one frame before it is pressed. The frame
         is always here and always the same height: the answer arrives into space that was already
         reserved, so the dialog does not grow under the customer's hand when it lands (W8-UI-01).
         Nothing stands in for a figure the platform has not measured — where there is no figure
         the frame carries the refusal in the platform's own words. -->
    <div class="flex h-[var(--train-estimate-height)] shrink-0 flex-col gap-[var(--measure-7px)] overflow-auto rounded-md border border-border p-2.5">
      <template v-if="estimate.length">
        <ParamRow v-for="e in estimate" :key="e.label" :label="e.label" :value="e.value" />
      </template>
      <!-- The sentence sits in the middle of the frame it shares with the figures, so a refusal
           reads as the frame's answer rather than as a line stranded at the top of an empty box. -->
      <p v-else-if="note" class="m-0 my-auto text-xs leading-relaxed text-pretty text-muted-foreground">{{ note }}</p>
      <div v-else class="flex flex-1 flex-col justify-around">
        <Skeleton v-for="row in RESERVED_ESTIMATE_ROWS" :key="row" :width="row % 2 ? 'var(--measure-160px)' : 'var(--measure-130px)'" />
      </div>
    </div>
    <!-- Outside the figures frame, because it is not one more figure: it is the one thing the
         customer needs before spending an hour on a run that will stop short. -->
    <div v-if="limitWarning" class="flex items-start gap-2 rounded-md border border-warning/40 px-2.5 py-2 text-xs"><Icon name="alert" :size="12" class="mt-0.5 shrink-0 text-warning" /><span>{{ limitWarning }}</span></div>
    </template>

    <template v-if="loading" #actions><ActionButton label="Cancel" @click="$emit('close')" /></template>
    <template v-else-if="!choosable" #actions><ActionButton label="Cancel" @click="$emit('close')" /></template>
    <template v-else #actions>
      <ActionButton label="Cancel" @click="$emit('close')" />
      <ActionButton label="Start training" variant="primary" :disabled="!set || !name.trim() || !ready || (!tabular && !baseModel) || (tabular && !targetColumn) || (!tabular && knobsInvalid)" @click="$emit('submit', { name: name.trim(), datasetId: set, acceleratorClass: accelerator, baseModel })">
        <template #icon><Icon name="play" /></template>
      </ActionButton>
    </template>
  </Dialog>
</template>
